import { gql } from "@apollo/client";

export const userFragment = gql`
  fragment user on User {
    id
    firstName
    lastName
    email
    phone
    lastLogin
    eligibleForStudy
    talentLmsUserId
    brainHqUserId
    brainGaugeAccessCode
    enrollmentConsent
    active
    confirmedRegistration
    cycleCompletionDate
    hasContentfulTrainingEnabled
    organization {
      id
      name
    }
    currentCycle {
      cycle {
        cycleNumber
      }
      userAssessmentCycle {
        id
        status
      }
    }
    lastCoachingSession {
      sessionTime
      coach {
        firstName
        lastName
      }
    }
    nextCoachingSession {
      sessionTime
      coach {
        firstName
        lastName
      }
    }
    aceCompleted
    role
    acuityOauthToken
  }
`;

export const nonParticipantUsers = gql`
  query Admins(
    $page: Int
    $limit: Int
    $searches: SearchObject
    $filters: [FilterObject!]
    $sort: OrderObject
  ) {
    nonParticipantUsers(
      page: $page
      limit: $limit
      searches: $searches
      filters: $filters
      sort: $sort
    ) {
      pageInfo {
        pageTotal
      }
      data {
        confirmedRegistration
        email
        firstName
        id
        lastName
        phone
        role
        active
      }
    }
  }
`;

export const participantUsers = gql`
  query Users(
    $page: Int
    $limit: Int
    $searches: SearchObject
    $filters: [FilterObject!]
    $sort: OrderObject
    $organizationId: String
  ) {
    participantUsers(
      page: $page
      limit: $limit
      searches: $searches
      filters: $filters
      sort: $sort
      organizationId: $organizationId
    ) {
      pageInfo {
        pageTotal
      }
      data {
        ...user
      }
    }
  }
  ${userFragment}
`;

export const allParticipantUsers = gql`
  query AllParticipantUsers($organizationId: String = "") {
    allParticipantUsers(organizationId: $organizationId) {
      ...user
    }
  }
  ${userFragment}
`;

export const Me = gql`
  query Me {
    me {
      ...user
    }
  }
  ${userFragment}
`;

export const ParticipantScores = gql`
  query AssessmentCyclesByParticipantId($filters: [FilterObject!], $sort: OrderObject!) {
    userAssessmentCycles(filters: $filters, sort: $sort) {
      data {
        id
        cycle {
          id
          cycleNumber
        }
        cycleNumber
        status
        cycleCompletionDate
        pillarScores {
          value
          pillarName
          userAssessments {
            name
            scores {
              scoreName
              scoreType
              value
            }
          }
        }
      }
    }
  }
`;

export const createNonParticipantUser = gql`
  mutation createNonParticipantUser($input: RegisterNonParticipantInput!) {
    createNonParticipantUser(input: $input) {
      id
      firstName
      lastName
      email
      role
      ianaTimeZoneName
    }
  }
`;

export const RegisterTalentLmsUser = gql`
  mutation RegisterTalentLmsUser($input: RegisterTalentLmsUserInput!) {
    registerTalentLmsUser(input: $input) {
      success
    }
  }
`;

export const AddBrainHqUser = gql`
  mutation AddBrainHqUser($input: BrainHqUserInput!) {
    addBrainHqUser(input: $input) {
      success
    }
  }
`;

export const AddBrainGaugeUser = gql`
  mutation addBrainGaugeAccessCodeForUser($input: BrainGaugeUserInput!) {
    addBrainGaugeAccessCodeForUser(input: $input) {
      success
    }
  }
`;

export const SetAceCompletedFlag = gql`
  mutation SetAceCompletedFlag($input: SetAceCompletedFlagInput!) {
    setAceCompletedFlag(input: $input) {
      success
    }
  }
`;

export const GetUserName = gql`
  query GetUser($id: ID!) {
    user(id: $id) {
      id
      firstName
      lastName
    }
  }
`;

export const GetUser = gql`
  query GetUser($id: ID!) {
    user(id: $id) {
      firstName
      lastName
      id
      email
      phone
      dob
      createdAt
      ianaTimeZoneName
      brainGaugeAccessCode
      organization {
        displayName
        id
      }
      lastLogin
      notifications {
        subjectLine
        createdAt
      }
      notificationsPreferences {
        twilioOptInRequired
        remindersEmail
        remindersSms
        trainingNotificationsEmail
        trainingNotificationsSms
        trainingNotificationsTime
        newsletterEmail
        habitNotificationsEmail
        habitNotificationsSms
        habitNotificationsTime
        partnerNotificationsEmail
        partnerNotificationsSms
        partnerNotificationsTime
        brainGaugeNotificationsEmail
        brainGaugeNotificationsSms
        brainGaugeNotificationsTime
      }
      currentCycle {
        cycle {
          cycleNumber
        }
        userAssessmentCycle {
          status
        }
      }
      tags {
        id
        label
      }
      badges {
        achievedAt
        badge {
          badgeImageKey
          description
          displayName
          name
          backgroundColor
        }
      }
    }
  }
`;

export const GetUserActivity = gql`
  query retrieveMatomoAnalytics($input: MatomoUserInput!) {
    retrieveMatomoAnalytics(input: $input) {
      userId
      lastLoginForPeriod
      totalVisitsForPeriod
      visitCount
      daysSinceFirstVisit
      visitHistoryForPeriod {
        visitDateTime
        totalTimeSpentforVisitInSeconds
        events {
          eventCategory
          timeSpentInSeconds
        }
      }
    }
  }
`;

export const RefreshToken = gql`
  mutation RefreshToken($token: String!) {
    refreshToken(token: $token)
  }
`;

export const GetTokenExpirationTime = gql`
  query TokenExpirationTime($token: String!) {
    tokenExpirationTime(token: $token)
  }
`;

export const ChangeEmailAddress = gql`
  mutation updateEmail($input: ChangeEmailInput!) {
    updateEmail(input: $input) {
      email
      id
    }
  }
`;
